/**
 * MifeLife v2 — AI Life Analysis flow (10 Likert + result).
 *
 * Steps: 0 = welcome, 1..10 = questions, 11 = loading, 12 = result.
 * Auth-required (POST /api/v2/analysis/submit needs the user).
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Loader2, Sparkles, Target, Brain, Zap, Compass, Repeat } from 'lucide-react';
import V2Nav from '../../components/v2/V2Nav';
import V2Footer from '../../components/v2/V2Footer';
import { apiRequest, getToken } from '../../lib/utils';

const COPY = {
  tr: {
    eyebrow: 'AI YAŞAM ANALİZİ',
    welcome: '10 soru. 2 dakika. AI senin profilini çıkarır.',
    welcomeSub: 'Odak, Enerji, Yön ve Rutinler boyutlarında nerede durduğunu gör. Sonunda sana uygun kurucu mentörleri öner.',
    start: 'Başla',
    next: 'Sonraki', prev: 'Geri', submit: 'Sonucu hesapla',
    progress: 'Soru', of: '/',
    scale: ['Hiç katılmıyorum', 'Katılmıyorum', 'Kararsızım', 'Katılıyorum', 'Tamamen katılıyorum'],
    loadingTitle: 'AI profilinizi çıkarıyor',
    loadingSub: 'Yol haritanız hazırlanıyor…',
    resultEyebrow: 'YOL HARİTAN HAZIR',
    resultIntro: 'Skorların ve sana özel 5-adımlı yol haritan aşağıda.',
    seeMentors: 'Sana uygun mentörleri gör',
    goDash: 'AI Asistan ile devam et',
    pdfSoon: 'PDF olarak indir',
    dims: { focus: 'Odak', energy: 'Enerji', direction: 'Yön', routines: 'Rutinler' },
    weakestNote: 'En düşük skor:',
    needAuth: 'Sonuçları kaydetmek için kayıt olmalısın.',
    needAuthCta: 'Hızlı kayıt ol',
    fail: 'Bir şey ters gitti. Lütfen tekrar dene.',
  },
};

const DIM_META = {
  focus:     { icon: Brain,   accent: 'violet'  },
  energy:    { icon: Zap,     accent: 'amber'   },
  direction: { icon: Compass, accent: 'cyan'    },
  routines:  { icon: Repeat,  accent: 'emerald' },
};

const ACCENT_BG = {
  violet:  'bg-violet-500/10 border-violet-400/30 text-violet-300',
  amber:   'bg-amber-500/10 border-amber-400/30 text-amber-300',
  cyan:    'bg-cyan-500/10 border-cyan-400/30 text-cyan-300',
  emerald: 'bg-emerald-500/10 border-emerald-400/30 text-emerald-300',
};

const ACCENT_BAR = {
  violet: 'bg-violet-400/80',
  amber: 'bg-amber-400/80',
  cyan: 'bg-cyan-400/80',
  emerald: 'bg-emerald-400/80',
};

export default function V2AnalysisPage() {
  const T = COPY.tr;
  const nav = useNavigate();
  const [step, setStep] = useState(0);        // 0 welcome, 1..10 question, 11 loading, 12 result
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    apiRequest('/v2/analysis/questions?language=tr').then((r) => setQuestions(r?.items || []));
  }, []);

  useEffect(() => {
    document.title = 'AI Yaşam Analizi · MifeLife';
  }, []);

  const total = questions.length || 10;
  const qIdx = step >= 1 && step <= total ? step - 1 : -1;
  const current = qIdx >= 0 ? questions[qIdx] : null;

  const select = (val) => {
    setAnswers((a) => ({ ...a, [current.id]: val }));
  };

  const goNext = async () => {
    if (step < total) {
      setStep(step + 1);
      return;
    }
    // Submit phase
    if (!getToken()) {
      // Save answers in sessionStorage and route to register with intent=back-to-analysis
      try { sessionStorage.setItem('v2_analysis_draft', JSON.stringify(answers)); } catch {}
      nav('/kayit?after=analiz');
      return;
    }
    setStep(11);
    setError(null);
    try {
      const res = await apiRequest('/v2/analysis/submit', {
        method: 'POST',
        body: JSON.stringify({ answers, language: 'tr' }),
      });
      setResult(res);
      setStep(12);
    } catch (e) {
      setError(e?.message || T.fail);
      setStep(total);
    }
  };

  const goPrev = () => setStep((s) => Math.max(0, s - 1));

  // ---------- render ----------
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0F0F1A] via-[#11111E] to-[#1A1A2E] text-white antialiased" data-testid="v2-analysis-page">
      <V2Nav lang="tr" />
      <main className="pt-28 sm:pt-32 pb-16">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">

          {step === 0 && (
            <div className="text-center pt-12 pb-20" data-testid="v2-analysis-welcome">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-500/10 border border-violet-400/30 text-[11px] uppercase tracking-[0.18em] text-violet-200 mb-6">
                <Sparkles className="w-3 h-3" /> {T.eyebrow}
              </div>
              <h1 className="text-3xl sm:text-5xl font-semibold tracking-tight leading-tight">{T.welcome}</h1>
              <p className="mt-5 text-base text-slate-400 max-w-xl mx-auto leading-relaxed">{T.welcomeSub}</p>
              <button
                onClick={() => setStep(1)}
                className="mt-10 inline-flex items-center gap-2 h-12 px-7 rounded-xl bg-gradient-to-r from-violet-500 to-violet-600 text-white text-sm font-semibold hover:from-violet-400 hover:to-violet-500 transition shadow-2xl shadow-violet-500/30 group"
                data-testid="v2-analysis-start"
              >
                {T.start} <ArrowRight className="w-4 h-4 transition group-hover:translate-x-1" />
              </button>
            </div>
          )}

          {step >= 1 && step <= total && current && (
            <div data-testid="v2-analysis-question">
              {/* Progress */}
              <div className="flex items-center justify-between mb-6">
                <span className="text-[11px] uppercase tracking-[0.18em] text-slate-500">{T.progress} {step}{T.of}{total}</span>
                <span className="text-[11px] uppercase tracking-[0.18em] text-violet-300">{T.dims[current.dim]}</span>
              </div>
              <div className="h-1 rounded-full bg-white/[0.05] overflow-hidden mb-10">
                <div className="h-full bg-gradient-to-r from-violet-500 to-cyan-400 transition-all duration-500" style={{ width: `${(step / total) * 100}%` }} />
              </div>

              <h2 className="text-2xl sm:text-3xl font-medium tracking-tight leading-snug mb-10" data-testid="v2-q-label">{current.label}</h2>

              <div className="space-y-2.5">
                {T.scale.map((opt, idx) => {
                  const val = idx + 1;
                  const sel = answers[current.id] === val;
                  return (
                    <button
                      key={val}
                      onClick={() => select(val)}
                      className={`w-full text-left rounded-xl border p-4 transition flex items-center gap-4 ${sel ? 'border-violet-400/60 bg-violet-500/10' : 'border-white/10 bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/20'}`}
                      data-testid={`v2-q-opt-${val}`}
                    >
                      <span className={`w-7 h-7 rounded-full border flex items-center justify-center text-[12px] font-medium tabular-nums ${sel ? 'border-violet-300/60 bg-violet-500/30 text-white' : 'border-white/15 text-slate-400'}`}>{val}</span>
                      <span className={`text-[14px] ${sel ? 'text-white' : 'text-slate-300'}`}>{opt}</span>
                    </button>
                  );
                })}
              </div>

              <div className="mt-10 flex items-center justify-between">
                <button onClick={goPrev} disabled={step === 1} className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white disabled:opacity-30 transition" data-testid="v2-q-prev">
                  <ArrowLeft className="w-4 h-4" /> {T.prev}
                </button>
                <button
                  onClick={goNext}
                  disabled={!answers[current.id]}
                  className="inline-flex items-center gap-1.5 h-11 px-5 rounded-lg bg-white text-slate-900 text-sm font-medium hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition group"
                  data-testid="v2-q-next"
                >
                  {step === total ? T.submit : T.next}
                  <ArrowRight className="w-4 h-4 transition group-hover:translate-x-0.5" />
                </button>
              </div>

              {error && <div className="mt-6 text-sm text-rose-300 text-center">{error}</div>}
            </div>
          )}

          {step === 11 && (
            <div className="text-center py-32" data-testid="v2-analysis-loading">
              <Loader2 className="w-8 h-8 text-violet-300 animate-spin mx-auto" />
              <h2 className="mt-6 text-xl font-medium tracking-tight">{T.loadingTitle}</h2>
              <p className="mt-2 text-sm text-slate-400">{T.loadingSub}</p>
            </div>
          )}

          {step === 12 && result && (
            <div data-testid="v2-analysis-result">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-400/30 text-[11px] uppercase tracking-[0.18em] text-emerald-200 mb-5">
                <Sparkles className="w-3 h-3" /> {T.resultEyebrow}
              </div>
              <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight mb-3">{result?.diagnosis}</h1>
              <p className="text-sm text-slate-400">{T.resultIntro}</p>

              <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3">
                {['focus','energy','direction','routines'].map((k) => {
                  const meta = DIM_META[k];
                  const v = result.scores?.[k] ?? 0;
                  return (
                    <div key={k} className={`rounded-xl border ${ACCENT_BG[meta.accent]} p-5`} data-testid={`v2-score-${k}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <meta.icon className="w-3.5 h-3.5" strokeWidth={1.8} />
                        <span className="text-[10px] uppercase tracking-[0.18em] opacity-80">{T.dims[k]}</span>
                      </div>
                      <div className="text-3xl font-semibold tabular-nums text-white">{v}</div>
                      <div className="mt-3 h-1 rounded-full bg-white/10 overflow-hidden">
                        <div className={`h-full ${ACCENT_BAR[meta.accent]}`} style={{ width: `${v}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Roadmap */}
              {Array.isArray(result.roadmap) && result.roadmap.length > 0 && (
                <div className="mt-12">
                  <div className="text-[11px] uppercase tracking-[0.18em] text-violet-300 mb-3">Yol Haritan</div>
                  <ul className="space-y-2.5">
                    {result.roadmap.map((it, i) => (
                      <li key={i} className="rounded-xl border border-white/10 bg-white/[0.02] p-4 flex gap-3" data-testid={`v2-roadmap-${i}`}>
                        <span className="text-[11px] tabular-nums text-violet-300 mt-0.5">{String(i + 1).padStart(2, '0')}</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-[14px] text-white leading-snug">{it.title}</div>
                          {it.why && <div className="text-xs text-slate-400 mt-1.5">{it.why}</div>}
                          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mt-2">{T.dims[it.category] || it.category}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* CTAs */}
              <div className="mt-12 flex flex-wrap items-center gap-3" data-testid="v2-result-ctas">
                <Link
                  to={`/mentorler?focus=${result.scores?.focus ?? ''}`}
                  className="inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-gradient-to-r from-violet-500 to-violet-600 text-white text-sm font-semibold hover:from-violet-400 hover:to-violet-500 transition shadow-lg shadow-violet-500/30 group"
                  data-testid="v2-result-cta-mentors"
                >
                  {T.seeMentors} <ArrowRight className="w-4 h-4 transition group-hover:translate-x-0.5" />
                </Link>
                <Link to="/app/ai-asistan" className="inline-flex items-center gap-1.5 h-11 px-4 rounded-lg border border-white/15 bg-white/[0.04] text-sm text-slate-200 hover:bg-white/[0.08] transition" data-testid="v2-result-cta-assistant">
                  {T.goDash}
                </Link>
                <span className="text-xs text-slate-500">{T.pdfSoon} — yakında</span>
              </div>
            </div>
          )}
        </div>
      </main>
      <V2Footer lang="tr" />
    </div>
  );
}
